<template>
  <div class="page-builder-search">
    <SectionsPageBanner heading="Search for a New Home Builder in your area" sub-heading="Builders search">
      <div class="__search-bar">
        <ElementsInputsText v-model="searchKeyword" class="__search-input" placeholder="Type builder name, region, service etc..." @update:model-value="setBuilderFilters()" />
        <ElementsInputsDropdown v-model="selectedService" :items="services" @update:model-value="setBuilderFilters()" />
        <ElementsButtonsRegular class="__action-primary-button" label="Search" :prepend-icon="searchPrependIcon" />
      </div>
    </SectionsPageBanner>
    <!-- Search Results -->
    <BuilderSearchResults />

    <LazyAdAdBar ad-display-location="builder_page_banner" />
  </div>
</template>

<script setup lang="ts">
import searchPrependIcon from '~/assets/images/icons/search.webp';
import {Ref} from 'vue';
import {useRoute, useRouter} from '#app';
import {LocationQuery} from 'vue-router';
import {useCompanyStore} from '~/store/company';
import {FilterBy} from '~/store/listing';

const companyStore = useCompanyStore();
const route = useRoute();
const router = useRouter();
const searchKeyword: Ref<string> = ref('');
const selectedService: Ref<string> = ref('');
const services: Ref<{ label: string; value: string }[]> = ref([
  {label: 'All Services', value: ''},
  {label: 'Land', value: 'Land'},
  {label: 'Apartment', value: 'Apartment'},
  {label: 'House & Land', value: 'House & Land'},
  {label: 'Ready to Move in', value: 'Ready to Move in'},
  {label: 'Turn Key', value: 'Turn Key'},
  {label: 'Affordable Housing', value: 'Affordable Housing'},
  {label: 'Design and Build', value: 'Design and Build'},
  {label: 'Showhome', value: 'Showhome'},
  {label: 'Investment', value: 'Investment'},
  {label: 'Terraced Housing', value: 'Terraced Housing'},
  {label: 'Architectural builder', value: 'Architectural builder'},
]);

/**
 * @_HEAD Hook
 */
useHead({
  meta: [
    {
      name: 'canonical',
      content: 'https://www.newhomes.co.nz' + route.fullPath,
    },
  ],
});

const title = 'Find Builders | New Homes - Search Top Home Builders in New Zealand';
const description = 'Discover top home builders in New Zealand. Enter a builder\'s name or services to view their listings, designs, and more on New Homes.';
useSeoMeta({
  title,
  description,
  ogTitle: title,
  ogDescription: description,
  ogImage: 'https://store.newhomeshub.co.nz/static-assets/new-homes-og-image-v2.jpg',
  twitterCard: 'summary_large_image',
  twitterTitle: title,
  twitterDescription: description,
  twitterImage: 'https://store.newhomeshub.co.nz/static-assets/new-homes-og-image-v2.jpg',
});

/**
 * @_MOUNTED Hook
 */
onMounted(() => {
  runFetch();
});

/**
 * Fetch search results
 */
async function runFetch () {
  initUrlParams(true);
// Call the initial listing fetch
  setBuilderFilters();
}

/**
 * Initiate URL params to filters
 */
function initUrlParams (setFromStore = false) {
  let params = route.query;

  if (setFromStore && companyStore && companyStore.urlParamObject) {
    const storeParams = companyStore.urlParamObject;
    if (JSON.stringify(storeParams) !== JSON.stringify(route.query)) {
      router.push({
        path: route.path,
        query: (storeParams as LocationQuery),
      });
      params = (storeParams as LocationQuery);
    }
  }

  if (params) {
    if (params.keyword) {
      searchKeyword.value = params.keyword as string;
    }
    if (params.selectedService) {
      selectedService.value = params.selectedService as string;
    }
  }
}

function setBuilderFilters () {
  const filterByItems: FilterBy[] = [];

  // Set property type
  filterByItems.push({field: 'services', value: selectedService.value});

  // Set filters and search keyword
  companyStore.setQueryOptions({
    filterBy: filterByItems,
    searchKeyword: searchKeyword.value,
  });

  // Set URL params
  setUrlParams();
}

/**
 * Set URL params
 */
function setUrlParams () {
  const params = {
    keyword: searchKeyword.value,
    selectedService: selectedService.value,
    perPage: companyStore.companytData.request_params.per_page,
    page: companyStore.companytData.page,
  };

  const {keyword, perPage, page} = route.query;
  if (JSON.stringify({keyword, selectedService: selectedService.value, perPage, page}) !== JSON.stringify({keyword: params.keyword, selectedService: params.selectedService, perPage: params.perPage, page: params.page})) {
    companyStore.setUrlParamObject(params);
    router.push({
      path: route.path,
      query: params,
    });
  }
}
</script>

<style lang="scss">
.page-builder-search {
  .__search-input {
    border-right: 1px solid #e5e5e5;
  }
}
</style>
